// Shared presentation for "what agent build is this device running?", mirrored
// from the backend services/agentVersion.js contract:
//
//   camera.agentVersion = { version, filename, sha256, appliedAt, status, target }
//
// `status` says how that build relates to what the device is SUPPOSED to run:
//   on-target  → running the current published/canary release
//   outdated   → a target exists and the device is behind it (rollout in flight)
//   no-target  → we know the build, but nothing is currently published
//   unknown    → the device has never completed an OTA through the DMS, so it is
//                still on whatever its SD-card image shipped with. The server
//                cannot know that build — say so rather than guess.

export const VERSION_STATUS = {
  ON_TARGET: "on-target",
  OUTDATED: "outdated",
  NO_TARGET: "no-target",
  UNKNOWN: "unknown",
};

export const VERSION_STATUS_TAG_COLOR = {
  [VERSION_STATUS.ON_TARGET]: "green",
  [VERSION_STATUS.OUTDATED]: "gold",
  [VERSION_STATUS.NO_TARGET]: "blue",
  [VERSION_STATUS.UNKNOWN]: "default",
};

export const VERSION_STATUS_LABEL = {
  [VERSION_STATUS.ON_TARGET]: "On target",
  [VERSION_STATUS.OUTDATED]: "Update pending",
  [VERSION_STATUS.NO_TARGET]: "No target set",
  [VERSION_STATUS.UNKNOWN]: "Never updated via DMS",
};

// What to print as the version itself. Unknown builds get an explicit label
// instead of an em dash, so "we don't know" is never confused with "no data".
export const IMAGE_BASELINE_LABEL = "Image baseline";

export function versionLabel(agentVersion) {
  if (!agentVersion) return IMAGE_BASELINE_LABEL;
  if (agentVersion.version) return agentVersion.version;
  if (agentVersion.filename) return agentVersion.filename;
  return IMAGE_BASELINE_LABEL;
}

// Long-form explanation for tooltips.
export function versionTooltip(agentVersion) {
  if (!agentVersion || agentVersion.status === VERSION_STATUS.UNKNOWN) {
    return "No agent update has been applied through the DMS, so this device is still running the build from the SD-card image it was flashed with.";
  }
  const parts = [];
  if (agentVersion.filename) parts.push(agentVersion.filename);
  if (agentVersion.sha256) parts.push(`sha256 ${agentVersion.sha256.slice(0, 12)}…`);
  if (agentVersion.status === VERSION_STATUS.OUTDATED && agentVersion.target?.version) {
    parts.push(`target: ${agentVersion.target.version} (${agentVersion.target.channel})`);
  }
  return parts.join("  ·  ");
}
